// src/components/CheckinDashboardTable.jsx
//
// Reusable table for a list of check-in records. The check-in analogue of
// CheckoutDashboardTable. Each row shows: student, grade, required-completion
// %, and a horizontal grid of status dots — one per item that applies to that
// student (by audience). Items that don't apply to a given student render as a
// faint dot.
//
// Props:
//   records: array of check-in record docs
//   resolvedItems: resolved item catalog (audience/required/signers)
//   user: current auth user (for "your pending" highlight)
//   onRowClick(record)
//   emptyMessage
//   defaultGradeFilter: 'all' | 'ms' | 'us' | specific grade string
//   hideGradeFilter: bool
//   hideDeadColumns: bool (default true) — hide item columns no visible record needs
//   toolbarExtra: optional render slot after the count
//   onVisibleRecordsChange(visibleRecords[])

import React, { useMemo, useState } from 'react';
import {
  ITEM_STATUS,
  RECORD_STATUS,
  MS_GRADES,
  US_GRADES,
  KA_COLORS,
  getRelevantCheckinItemIds,
  computeCheckinCompletion,
  canCheckInItem,
} from '../utils/checkinHelpers';

// Short column headers — keeps the vertical headers narrow.
const ITEM_ABBR = {
  finance: 'Finance',
  handbook: 'Handbook',
  campus_entry: 'Entry',
  health_screening: 'Health',
  insurance: 'Insurance',
  photo_policy: 'Photo',
  transportation: 'Transport',
  grandparent_info: 'Grandpar.',
  contact_verification: 'Contact',
  communication_agreement: 'Comms',
  day_bus_service: 'Bus',
  boarding_permission: 'Boarding',
  cake_order: 'Cake',
  laundry: 'Laundry',
  academics_registration: 'Academics',
  immigration_visa: 'Visa',
};

const styles = {
  wrapper: { width: '100%' },
  controls: {
    display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
    marginBottom: 14, padding: 12, background: KA_COLORS.lightGray,
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 6,
  },
  searchInput: {
    flex: 1, minWidth: 200, padding: '6px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4, fontSize: 13,
  },
  filterSelect: {
    padding: '6px 10px', border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4, fontSize: 13, background: '#fff', cursor: 'pointer',
  },
  countLabel: { fontSize: 12, color: '#666', fontWeight: 600 },
  tableWrapper: {
    overflowX: 'auto', border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6, background: '#fff',
  },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 13,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  th: {
    background: KA_COLORS.navy, color: '#fff', padding: '10px 8px',
    textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, whiteSpace: 'nowrap', position: 'sticky', top: 0,
  },
  thItem: {
    background: KA_COLORS.navy, color: '#fff', padding: '10px 4px', fontSize: 9,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'center',
    writingMode: 'vertical-rl', transform: 'rotate(180deg)',
    minWidth: 22, height: 110, whiteSpace: 'nowrap',
  },
  tr: (clickable) => ({
    cursor: clickable ? 'pointer' : 'default', transition: 'background 0.1s',
    borderBottom: `1px solid ${KA_COLORS.borderGray}`,
  }),
  trHover: { background: '#FFF5F5' },
  td: { padding: '8px', verticalAlign: 'middle', borderRight: `1px solid ${KA_COLORS.borderGray}` },
  studentNameCell: { fontWeight: 600, color: KA_COLORS.navy, fontSize: 13 },
  studentMeta: { fontSize: 11, color: '#666' },
  gradePill: (grade) => {
    const colors = {
      'Grade 7': '#00ACC1', 'Grade 8': '#E65100',
      'Grade 9': '#1976D2', 'Grade 10': '#00796B',
      'Grade 11': '#F57C00', 'Grade 12': '#7B1FA2',
    };
    const color = colors[grade] || '#555';
    return {
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      background: color, color: '#fff', fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
    };
  },
  pctCell: { textAlign: 'center', whiteSpace: 'nowrap' },
  progressMini: {
    width: 60, height: 6, background: KA_COLORS.borderGray,
    borderRadius: 3, overflow: 'hidden', margin: '4px auto 0',
  },
  progressMiniFill: (pct, complete) => ({
    width: `${pct}%`, height: '100%', background: complete ? '#2E7D32' : KA_COLORS.gold,
  }),
  pctLabel: { fontSize: 12, fontWeight: 700, color: KA_COLORS.navy },
  itemCell: { padding: '4px 2px', textAlign: 'center', borderRight: `1px solid ${KA_COLORS.borderGray}` },
  statusDot: (status, isMine) => {
    const palette = {
      [ITEM_STATUS.SIGNED]: { bg: '#2E7D32', fg: '#fff', char: '✓' },
      [ITEM_STATUS.NA]: { bg: '#E0E0E0', fg: '#888', char: '–' },
      [ITEM_STATUS.PENDING]: { bg: '#FFC107', fg: '#000', char: '·' },
    };
    const p = palette[status] || palette[ITEM_STATUS.PENDING];
    return {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 20, height: 20, borderRadius: '50%', background: p.bg, color: p.fg,
      fontSize: 12, fontWeight: 700,
      border: isMine ? `2px solid ${KA_COLORS.red}` : '1px solid transparent',
      boxSizing: 'border-box',
    };
  },
  emptyState: { padding: 40, textAlign: 'center', color: '#999', fontStyle: 'italic', fontSize: 13 },
  legend: {
    display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10,
    fontSize: 11, color: '#555', alignItems: 'center',
  },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 4 },
};

function resolveDefaultGradeFilter(value) {
  if (value == null) return 'all';
  if (Array.isArray(value)) {
    if (value.length === MS_GRADES.length && value.every((g) => MS_GRADES.includes(g))) return 'ms';
    if (value.length === US_GRADES.length && value.every((g) => US_GRADES.includes(g))) return 'us';
    if (value.length === 1) return value[0];
    return 'all';
  }
  return value;
}

const ItemDot = ({ item, record, user, resolvedItems }) => {
  const relevantIds = getRelevantCheckinItemIds(record, resolvedItems);
  const isRelevant = relevantIds.includes(item.id);
  if (!isRelevant) {
    return <span style={{ color: '#ccc', fontSize: 12 }} title="Not applicable to this student"> · </span>;
  }
  const status = record.items?.[item.id]?.status || ITEM_STATUS.PENDING;
  const isMyPending = status === ITEM_STATUS.PENDING && canCheckInItem(user, item);
  const palette = {
    [ITEM_STATUS.SIGNED]: '✓',
    [ITEM_STATUS.NA]: '–',
    [ITEM_STATUS.PENDING]: '·',
  };
  const char = palette[status] || '·';
  const data = record.items?.[item.id];
  const tooltip = [item.label, status, data?.signedByName && `by ${data.signedByName}`]
    .filter(Boolean).join(' — ');
  return (
    <span style={styles.statusDot(status, isMyPending)} title={tooltip}>{char}</span>
  );
};

const TableRow = ({ record, user, onRowClick, visibleItems, resolvedItems }) => {
  const [hover, setHover] = useState(false);
  const completion = computeCheckinCompletion(record, resolvedItems);
  const isComplete = completion === 100;
  return (
    <tr
      style={{ ...styles.tr(!!onRowClick), ...(hover ? styles.trHover : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onRowClick && onRowClick(record)}
    >
      <td style={styles.td}>
        <div style={styles.studentNameCell}>{record.studentName || '—'}</div>
        <div style={styles.studentMeta}>{[record.cohort].filter(Boolean).join(' • ')}</div>
      </td>
      <td style={styles.td}>
        <span style={styles.gradePill(record.grade)}>{record.grade || '—'}</span>
      </td>
      <td style={{ ...styles.td, ...styles.pctCell }}>
        <div style={styles.pctLabel}>{completion}%</div>
        <div style={styles.progressMini}>
          <div style={styles.progressMiniFill(completion, isComplete)} />
        </div>
      </td>
      {visibleItems.map((item) => (
        <td key={item.id} style={styles.itemCell}>
          <ItemDot item={item} record={record} user={user} resolvedItems={resolvedItems} />
        </td>
      ))}
    </tr>
  );
};

const CheckinDashboardTable = ({
  records = [],
  resolvedItems = [],
  user,
  showSearchAndFilters = true,
  emptyMessage = 'No check-in records to display.',
  onRowClick,
  toolbarExtra,
  onVisibleRecordsChange,
  defaultGradeFilter = 'all',
  hideGradeFilter = false,
  hideDeadColumns = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState(() => resolveDefaultGradeFilter(defaultGradeFilter));
  const [itemFilter, setItemFilter] = useState('all');
  const [itemMode, setItemMode] = useState('pending'); // 'pending' | 'applies'
  const [statusFilter, setStatusFilter] = useState('all');

  const visibleRecords = useMemo(() => {
    let out = records;

    if (gradeFilter !== 'all') {
      if (gradeFilter === 'ms') out = out.filter((r) => MS_GRADES.includes(r.grade));
      else if (gradeFilter === 'us') out = out.filter((r) => US_GRADES.includes(r.grade));
      else out = out.filter((r) => r.grade === gradeFilter);
    }

    if (itemFilter !== 'all') {
      if (itemMode === 'pending') {
        out = out.filter((r) => r.items?.[itemFilter]?.status === ITEM_STATUS.PENDING);
      } else {
        out = out.filter((r) =>
          getRelevantCheckinItemIds(r, resolvedItems).includes(itemFilter),
        );
      }
    }

    if (statusFilter === 'in_progress') {
      out = out.filter(
        (r) => r.status !== RECORD_STATUS.COMPLETE && computeCheckinCompletion(r, resolvedItems) < 100,
      );
    } else if (statusFilter === 'complete') {
      out = out.filter(
        (r) => r.status === RECORD_STATUS.COMPLETE || computeCheckinCompletion(r, resolvedItems) === 100,
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      out = out.filter((r) => {
        const name = (r.studentName || '').toLowerCase();
        const sis = String(r.studentId || '').toLowerCase();
        const grade = (r.grade || '').toLowerCase();
        return name.includes(q) || sis.includes(q) || grade.includes(q);
      });
    }
    return out;
  }, [records, resolvedItems, searchTerm, gradeFilter, itemFilter, itemMode, statusFilter]);

  // Which item columns to show: when hideDeadColumns, only items relevant to
  // at least one visible record.
  const visibleItems = useMemo(() => {
    if (!hideDeadColumns || visibleRecords.length === 0) return resolvedItems;
    return resolvedItems.filter((item) =>
      visibleRecords.some((r) => getRelevantCheckinItemIds(r, resolvedItems).includes(item.id)),
    );
  }, [resolvedItems, visibleRecords, hideDeadColumns]);

  React.useEffect(() => {
    if (typeof onVisibleRecordsChange === 'function') onVisibleRecordsChange(visibleRecords);
  }, [visibleRecords, onVisibleRecordsChange]);

  return (
    <div style={styles.wrapper}>
      {showSearchAndFilters && (
        <div style={styles.controls}>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Search by name, grade, or SIS ID…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!hideGradeFilter && (
            <select style={styles.filterSelect} value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} title="Filter by grade">
              <option value="all">All grades</option>
              <option value="ms">— Middle School (7–8) —</option>
              <option value="Grade 7">Grade 7</option>
              <option value="Grade 8">Grade 8</option>
              <option value="us">— Upper School (9–12) —</option>
              <option value="Grade 9">Grade 9</option>
              <option value="Grade 10">Grade 10</option>
              <option value="Grade 11">Grade 11</option>
              <option value="Grade 12">Grade 12</option>
            </select>
          )}
          <select style={styles.filterSelect} value={itemFilter} onChange={(e) => setItemFilter(e.target.value)} title="Filter by item">
            <option value="all">All items</option>
            {resolvedItems.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
          {itemFilter !== 'all' && (
            <select style={styles.filterSelect} value={itemMode} onChange={(e) => setItemMode(e.target.value)} title="Item filter mode">
              <option value="pending">Pending only</option>
              <option value="applies">All who need it</option>
            </select>
          )}
          <select style={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="in_progress">In progress</option>
            <option value="complete">Complete</option>
          </select>
          <span style={styles.countLabel}>{visibleRecords.length} of {records.length}</span>
          {toolbarExtra}
        </div>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Student</th>
              <th style={styles.th}>Grade</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Progress</th>
              {visibleItems.map((item) => (
                <th key={item.id} style={styles.thItem}>{ITEM_ABBR[item.id] || item.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRecords.length === 0 ? (
              <tr>
                <td colSpan={3 + visibleItems.length} style={styles.emptyState}>{emptyMessage}</td>
              </tr>
            ) : (
              visibleRecords.map((record) => (
                <TableRow
                  key={record.id}
                  record={record}
                  user={user}
                  onRowClick={onRowClick}
                  visibleItems={visibleItems}
                  resolvedItems={resolvedItems}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.legend}>
        <span style={styles.legendItem}><span style={styles.statusDot(ITEM_STATUS.SIGNED, false)}>✓</span> Cleared</span>
        <span style={styles.legendItem}><span style={styles.statusDot(ITEM_STATUS.PENDING, false)}>·</span> Pending</span>
        <span style={styles.legendItem}><span style={styles.statusDot(ITEM_STATUS.NA, false)}>–</span> N/A</span>
        <span style={styles.legendItem}>
          <span style={styles.statusDot(ITEM_STATUS.PENDING, true)}>·</span>
          <span style={{ color: KA_COLORS.red, fontWeight: 600 }}>Pending — your item</span>
        </span>
      </div>
    </div>
  );
};

export default CheckinDashboardTable;